import { Badge, cn, Progress } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { CircleCheck, CirclePause, TriangleAlert } from 'lucide-react';
import { StatusChip } from '@/components/ui/status-chip';
import type { MmProgramSummary } from '@/services/api/mm-programs';
import { describeDaysFromToday, utcDaysBetween } from '@/utils/date';
import { formatUsd } from '@/utils/format';
import { CHANGE_LABELS } from './dashboard';

const StatusCell = ({ marketMaker }: { marketMaker: MmProgramSummary }) => (
  <div className="flex flex-wrap gap-1.5">
    {marketMaker.problems.length ? (
      <StatusChip className="px-2 py-0.5 text-xs" icon={TriangleAlert} variant="destructive">
        Invalid
      </StatusChip>
    ) : null}
    <StatusChip className="px-2 py-0.5 text-xs" icon={marketMaker.active ? CircleCheck : CirclePause} variant={marketMaker.active ? 'success' : 'muted'}>
      {marketMaker.active ? 'Active' : 'Inactive'}
    </StatusChip>
  </div>
);
StatusCell.displayName = 'StatusCell';

const InstrumentsCell = ({ marketMaker }: { marketMaker: MmProgramSummary }) => {
  if (!marketMaker.instruments.length) return <span className="text-xs text-muted-foreground">No instruments</span>;
  const ordered = [...marketMaker.instruments].sort((left, right) => Number(right.live) - Number(left.live) || left.instrument.localeCompare(right.instrument));
  return (
    <div className="flex max-w-[22rem] flex-wrap gap-1">
      {ordered.map((summary) => (
        <Badge
          className={cn('rounded px-1.5 font-mono text-[11px] font-normal', !summary.live && 'border-dashed text-muted-foreground')}
          key={summary.instrument}
          title={summary.live ? `In force since ${summary.since}` : 'No schedule in force today'}
          variant="outline"
        >
          {summary.instrument}
          {summary.live ? null : <span className="sr-only"> (no schedule in force today)</span>}
        </Badge>
      ))}
    </div>
  );
};
InstrumentsCell.displayName = 'InstrumentsCell';

const CompensationCell = ({ value, max, muted }: { value: number; max: number; muted: boolean }) => (
  <div className="flex min-w-[9rem] flex-col gap-1">
    <span className={muted ? 'tabular-nums text-muted-foreground' : 'tabular-nums font-medium'}>{formatUsd(value, 0)}</span>
    <Progress aria-hidden className={cn('h-1 md:h-1', muted && '[&>div]:bg-muted-foreground/40')} size="sm" value={max > 0 ? (value / max) * 100 : 0} />
  </div>
);
CompensationCell.displayName = 'CompensationCell';

const NextChangeCell = ({ marketMaker, asOf }: { marketMaker: MmProgramSummary; asOf: string }) => {
  const change = marketMaker.nextChange;
  if (!change) return <span className="text-xs text-muted-foreground">Nothing scheduled</span>;
  return (
    <div className="flex flex-col">
      <span className="text-sm">
        {CHANGE_LABELS[change.kind]} <span className="font-mono text-xs">{change.instrument}</span>
      </span>
      <span className="text-xs text-muted-foreground tabular-nums">
        {change.date} · {describeDaysFromToday(utcDaysBetween(asOf, change.date))}
      </span>
    </div>
  );
};
NextChangeCell.displayName = 'NextChangeCell';

export const buildMmProgramColumns = (asOf: string, maxMonthlyUsd: number): ColumnDef<MmProgramSummary>[] => [
  {
    accessorKey: 'name',
    header: 'Market maker',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium">{row.original.name}</span>
        <span className="text-xs text-muted-foreground tabular-nums">FAST {row.original.fast_id}</span>
      </div>
    ),
  },
  { id: 'status', accessorFn: (row) => (row.problems.length ? 0 : row.active ? 1 : 2), header: 'Status', cell: ({ row }) => <StatusCell marketMaker={row.original} /> },
  { id: 'instruments', accessorKey: 'liveInstruments', header: 'Instruments', cell: ({ row }) => <InstrumentsCell marketMaker={row.original} /> },
  {
    accessorKey: 'monthlyCompensationUsd',
    header: 'Monthly at full KPI',
    cell: ({ row }) => <CompensationCell max={maxMonthlyUsd} muted={!row.original.active} value={row.original.monthlyCompensationUsd} />,
  },
  {
    id: 'nextChange',
    accessorFn: (row) => row.nextChange?.date ?? '9999-12-31',
    header: 'Next change',
    cell: ({ row }) => <NextChangeCell asOf={asOf} marketMaker={row.original} />,
  },
];
